import ResizeObserver from 'resize-observer-polyfill';
import {AttrsSchema} from 'panel';
import {Hooks} from 'snabbdom';
import type {
  Instance as PopperInstance,
  ModifierArguments,
  Placement as PopperPlacement,
  State as PopperState,
  VirtualElement,
} from '@popperjs/core';
import {arrow, computeStyles, createPopperLite, flip, hide, offset, preventOverflow} from '@popperjs/core';

import {StackableComponent, Visibility, VisibilityChangeEvent} from 'common/components/base/stackable';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {CHROME_HEADER_HEIGHT} from 'common/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {scrollIntoViewIfNeeded} from 'common/util/dom';
import {portalify} from 'common/util/experimental-portal';

import {Directions, PositioningStrategy, PositionParentType} from './types';
import {template} from './template';
import css from './index.inline.styl';

export interface DropMenuAttrs {
  'allowed-flip-directions'?: Directions[];
  'disable-auto-focus'?: boolean;
  'experimental-positioning'?: boolean;
  'position-strategy'?: PositioningStrategy;
  'prevent-close-selectors'?: string[];
  'prevent-close'?: boolean;
  'show-arrow'?: boolean;
  direction?: Directions;
  elRef?: string;
  offset?: number;
  open?: boolean;
  skidding?: number;
}

export type PositionParent = PositionParentType | HTMLElement | VirtualElement;

export interface DropMenuProps {
  attrs?: DropMenuAttrs;
  on?: {
    change?: (ev: VisibilityChangeEvent) => void;
  };
  props?: {
    positionParent?: PositionParent;
  };
  hook?: Hooks;
}

// defaults positioning to popper's edge alignment
export const POPPER_POSITION_OVERRIDES: {[key in Directions]: PopperPlacement} = {
  [Directions.Down]: `bottom-start`,
  [Directions.DownRight]: `bottom-end`,
  [Directions.Up]: `top-start`,
  [Directions.Right]: `right-start`,
  [Directions.CenterRight]: `right`,
  [Directions.Left]: `left-start`,
  [Directions.CenterUp]: `top`,
  [Directions.CenterDown]: `bottom`,
};

const DIRECTION_VERTICALITY_MODIFIERS = {
  [Directions.Right]: 0,
  [Directions.Left]: 0,
  [Directions.Up]: 1,
  [Directions.Down]: -1,
};

const PADDING_FROM_BOTTOM = 60;

export class DropMenu extends StackableComponent<EmptyObj, DropMenuAttrs, IronContextRegistry> {
  private dropMenuOpenAt: number = null;
  private scrollParent: HTMLElement = null;
  private scrollParentPaddingBottom: string = null;
  protected popperInstance: PopperInstance = null;
  private resizeObserver: ResizeObserver;
  private topPosition: number = null;
  private lastPlacement: PopperPlacement = null;
  private skipFlip = false;
  private hasScrolledIntoView = false;
  private maxPadding = 0;

  /** proves an override to the reference element the dropdown will align to */
  private _positionParent: PositionParent = PositionParentType.OffsetParent;

  static get attrsSchema(): AttrsSchema<DropMenuAttrs> {
    return {
      ...super.attrsSchema,
      open: {
        description: `Is the dropdown menu open?`,
        type: `boolean`,
      },
      offset: {type: `number`, default: 4},
      skidding: {type: `number`, default: 0, description: `Amount of shifting parallel to parent`},
      'show-arrow': {type: `boolean`, description: `Whether to display an arrow b/w the tooltip and parent`},
      direction: {
        description: `Menu opens on the top of an element if 'up', on the bottom if 'down'`,
        type: `string`,
        enum: Object.values(Directions),
        default: Directions.Down,
      },
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Fixed,
        description: `Defines how the menu is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
      'allowed-flip-directions': {
        type: `json`,
        default: null,
        description: `Defines preferred flipping directions for whenever the dropdown cannot be fitted in view`,
      },
      'experimental-positioning': `boolean`,
      'prevent-close': {
        type: `boolean`,
        description: `When true, prevent drop menu from closing by regular means such as clicking outside of the component.`,
      },
      'disable-auto-focus': {
        type: `boolean`,
        description: `When true, prevent default StackableComponent behavior of focusing the first focusable child when the menu is open.`,
      },
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
    };
  }

  set positionParent(positionParent: PositionParent) {
    positionParent ||= PositionParentType.OffsetParent;

    if (this._positionParent !== positionParent) {
      this._positionParent = positionParent;
      this.popperInstance?.destroy();
      this.popperInstance = null;
      this.initialized && this.state.visibility === Visibility.Open && this.setupPopper();
    } else {
      this.popperInstance?.update();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.attr(`open`)) {
      this.open();
    }
  }

  disconnectedCallback() {
    this.popperInstance?.destroy();
    this.popperInstance = null;
    super.disconnectedCallback();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (this.initialized && attr === `open`) {
      if (this.attr(`open`)) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  get trapTab() {
    return false;
  }

  /**
   * When a dropdown is absolute positioned relative to another container we
   * make the assumption that the container is fixed and is the top most stacking
   * context in the page, in that case we want the dropdown to display as a tooltip
   */
  get increaseStackingContext() {
    return this.attr(`position-strategy`) === PositioningStrategy.Fixed;
  }

  get createPopper() {
    return createPopperLite;
  }

  /**
   * Setups the positioning library for the dropdown, for more info see:
   * https://popper.js.org/docs/v2/
   */
  setupPopper() {
    const isFixed = this.attr(`position-strategy`) === PositioningStrategy.Fixed;

    const positionParent = typeof this._positionParent === `string` ? this[this._positionParent] : this._positionParent;

    // if there's no parent element to attach to, this either means the dropdown's parent element has been
    // disconnected and out of view, setting up popper.js is not needed in those cases
    if (!positionParent) {
      return null;
    }

    // popper.js does not flip placement variations (*-start or *-end) to their opposite corner if that
    // placement overflows from the alternate axis (if bottom, this is the X axis), even though the page
    // might scroll to display the overflowing area from the alternative axis, we avoid
    // that situation by defaulting to the *-end position of the desired placement
    let placement = POPPER_POSITION_OVERRIDES[this.attr(`direction`)];
    if (this.getBoundingClientRect().right >= window.innerWidth) {
      if (placement.startsWith(`bottom`)) {
        placement = `bottom-end`;
      } else if (placement.startsWith(`top`)) {
        placement = `top-end`;
      }
    }

    // initializes popper for the first time the dropdown is being displayed
    !this.popperInstance &&
      (this.popperInstance = this.createPopper(positionParent, this, {
        onFirstUpdate: () => {
          window.requestAnimationFrame(() => (this.skipFlip = true));
        },
      }));

    // dropdowns that are defined within the body scrolling area should be fitted within
    // the body element itself, dropdowns within scrollable areas will be fitted to the
    // viewport so that they always land in view regardless of the body scrolling position
    const scrollParent = this.popperInstance.state.scrollParents?.reference[0] ?? window;
    const isBodyLevel = (scrollParent as unknown) === window;

    // default to viewport if placing dropdown at document root level
    const rootBoundary = this.attr(`experimental-positioning`)
      ? `viewport`
      : this.offsetHeight > window.innerHeight
      ? `document`
      : `viewport`;

    const referenceEl = this.popperInstance.state.elements.reference;
    this.scrollParent = isBodyLevel ? document.body : (scrollParent as HTMLElement);
    this.scrollParentPaddingBottom = this.scrollParent.style.paddingBottom;

    const scrollParentOffset = isBodyLevel ? document.documentElement.scrollTop : this.scrollParent.scrollTop;
    const vertOffset = DIRECTION_VERTICALITY_MODIFIERS[this.attr(`direction`)] * this.attr(`offset`);

    // stores initial top position of the dropdown so that it remains static as the window resizes
    // or its parent is scrolled, this is always relative to its scrolling container
    const referenceElRect = referenceEl.getBoundingClientRect();
    this.topPosition =
      referenceElRect.top -
      (isBodyLevel ? 0 : this.scrollParent.getBoundingClientRect().top) +
      scrollParentOffset +
      referenceElRect.height +
      vertOffset;

    this.popperInstance.setOptions({
      placement,
      strategy: this.attr(`position-strategy`),
      modifiers: [
        // adds attributes to define if the offsetParent becomes hidden to properly
        // hide the dropdown so that it does not appear floating outside of a scrolling container
        preventOverflow,
        {
          ...hide,
          name: `hide`,
          enabled: isFixed,
        },
        this.attr(`show-arrow`)
          ? {
              ...arrow,
              name: `arrow`,
              options: {
                padding: 4,
                element: this.el.querySelector(`.mp-drop-menu-arrow`),
              },
            }
          : null,
        // defines spacing between the dropdown and its offsetParent
        {
          ...offset,
          name: `offset`,
          options: {offset: [this.attr(`skidding`), this.getOffset()]},
        },
        // makes the dropdown align itself so that it
        // is visible inside the window viewport, it also
        // prevents flipping the dropdown when its positioning
        // has already been flipped
        {
          ...flip,
          name: `flip`,
          data: {
            ...flip.data,
            skipFlip: false,
          },
          fn: (args: ModifierArguments<UnknownObj>) => {
            if (
              !this.skipFlip ||
              this.attr(`position-strategy`) === PositioningStrategy.Absolute ||
              this.attr(`experimental-positioning`)
            ) {
              return flip.fn(args);
            } else if (args.state.modifiersData[`flip`].skipFlip === false) {
              args.state.placement = this.lastPlacement;
              args.state.reset = true;
              args.state.modifiersData[`flip`].skipFlip = true;
            }
          },
          options: {
            rootBoundary,
            // prevents dropdown from going over the sticky navigation bar
            padding: isFixed ? {top: CHROME_HEADER_HEIGHT} : {},
            ...(Array.isArray(this.attr(`allowed-flip-directions`))
              ? {fallbackPlacements: this.attr(`allowed-flip-directions`)}
              : {}),
          },
        },
        // disable the use of transform to position the dropdown so that any
        // slotted content that is fixed (tooltips, context panel, etc) does
        // not become relative to the dropdown stacking context
        {
          ...computeStyles,
          name: `computeStyles`,
          options: {
            /** @todo [pablo] alternatively, we can define slotted stacking contexts as absolute
            to become relative to the top fixed stacking context (the dropdown that is active) */
            gpuAcceleration: this.attr(`experimental-positioning`),
          },
        },
        // custom modifier that adds bottom padding to the scrolling parent if
        // the dropdown cannot be fitted in the viewport, this is to mimic
        // absolute positioned dropdowns which increase the offsetHeight of its scrolling
        // parent whereas fixed position elements do not
        {
          name: `scrollingPadding`,
          enabled: isFixed && this.attr(`experimental-positioning`) === false,
          phase: `afterMain`,
          requires: [`flip`],
          fn: ({state}: {state: PopperState}) => this.popperComeIntoViewModifier(state, referenceElRect),
        },
      ].filter(Boolean),
    });

    return this.popperInstance;
  }

  popperComeIntoViewModifier(state: PopperState, referenceElRect: ClientRect) {
    const isBottomAligned = state.placement.startsWith(`bottom`);
    const paddingBottom = Number.parseInt(window.getComputedStyle(this.scrollParent).paddingBottom, 10);
    const popperBottom = this.topPosition + state.rects.popper.height;
    const parentScrollableDiff = popperBottom - (this.scrollParent.scrollHeight - paddingBottom);
    const overflowsViewPort =
      referenceElRect.bottom + state.rects.popper.height + this.attr(`offset`) >= window.innerHeight;

    const willOverflow = isBottomAligned && overflowsViewPort && parentScrollableDiff > 0;

    this.lastPlacement = state.placement;
    const newPadding = parentScrollableDiff + PADDING_FROM_BOTTOM;

    if (!this.scrollParentPaddingBottom && newPadding > this.maxPadding && willOverflow) {
      this.scrollParent.style.paddingBottom = `${newPadding}px`;
      this.maxPadding = newPadding;
    }

    // for dropdowns inside scrollable containers we want to bring the bottom
    // of the dropdown into view
    if (!this.hasScrolledIntoView && this.attr(`position-strategy`) === PositioningStrategy.Fixed) {
      this.hasScrolledIntoView = true;
      window.requestAnimationFrame(() => scrollIntoViewIfNeeded(this, this.scrollParent));
    }
  }

  protected onOpen() {
    super.onOpen();

    this.dropMenuOpenAt = Date.now();
    const initPopper = () => {
      if (!this.popperInstance) {
        this.setupPopper();
        // if content is added to the document, fixed positioned dropdowns need to reflect the updated
        // position relative to its parent element
        this.resizeObserver?.observe(document.body);
        this.scrollParent && this.resizeObserver?.observe(this.scrollParent);
      }
    };

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (this.popperInstance) {
          const isSelfTarget = entries.some((entry) => entry.target === this);
          this.hasScrolledIntoView = isSelfTarget ? false : this.hasScrolledIntoView;
          this.popperInstance?.update().then(() =>
            this.dispatchEvent(
              new CustomEvent<{name: string; openedAt: number}>(`perfpoint`, {
                detail: {name: `drop-menu update`, openedAt: this.dropMenuOpenAt},
              }),
            ),
          );
        } else {
          initPopper();
        }
      });
    }

    requestAnimationFrame(() => initPopper());

    this.resizeObserver.observe(this);
  }

  protected getOffset() {
    // arrow + normal offset
    return this.attr(`offset`) + (this.attr(`show-arrow`) ? 4 : 0);
  }

  protected onClose() {
    super.onClose();
    this.resizeObserver?.disconnect();
    this.popperInstance?.destroy();
    this.popperInstance = null;
    this.resizeObserver = null;
    this.skipFlip = false;
    this.hasScrolledIntoView = false;
    this.maxPadding = 0;
    this.removeAttribute(`data-popper-placement`);

    // remove added vertical padding to scrolling container
    this.scrollParent &&
      this.attr(`position-strategy`) === PositioningStrategy.Fixed &&
      (this.scrollParent.style.paddingBottom = this.scrollParentPaddingBottom);
  }

  isCloseable() {
    return !this.attr(`prevent-close`);
  }

  get shouldAutoFocus(): boolean {
    return !this.attr(`disable-auto-focus`);
  }
}

export const DropMenuEl = defineAndMakeJsxFactory<DropMenuProps>(`mp-drop-menu`, DropMenu);
export const DropMenuReact = panel2react<DropMenuProps>(`mp-drop-menu`, DropMenu);
export const ExperimentalDropMenuEl = portalify(DropMenuEl);
